/**
 * A long page split into sections, one shown at a time, with a menu of them —
 * down the side on a wide screen, a row of pills on a phone.
 *
 * A settings page of eight cards is a page nobody can see the shape of; its
 * menu says what is there and takes you to it. The menu sticks under the
 * page's fixed header (`--page-sticky-top`, published by `PageHeader`), so it
 * is always in reach, and the chosen section is in the URL (`?section=`), so a
 * link or the back button returns to it.
 *
 * **Every section stays mounted; only the chosen one is shown.** A section is
 * usually part of one form with one Save: switching must not lose what was
 * typed in another, and a Save must still send all of it. When the browser
 * refuses a Save because a field is invalid in a hidden section, that section
 * is opened, so the person sees what to fix rather than a button that does
 * nothing.
 */

import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { ScrollArea } from './scroll-area';

export interface SectionItem {
  id: string;
  label: ReactNode;
  /** A line under the label in the side menu (wide screens only). */
  hint?: ReactNode;
  icon?: ReactNode;
  /** Beside the label: a count, a "needs attention" dot. */
  badge?: ReactNode;
  content: ReactNode;
}

export interface SectionsProps {
  items: readonly SectionItem[];
  /** Controlled: the chosen section. Otherwise it lives in the URL. */
  value?: string;
  onChange?: (id: string) => void;
  /** The URL parameter that holds the chosen section. */
  param?: string;
  /** What the menu is called, for screen readers. */
  label?: string;
  className?: string;
}

function useChosen(items: readonly SectionItem[], param: string, value?: string, onChange?: (id: string) => void) {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get(param);
  const chosen =
    value ?? (items.some((item) => item.id === fromUrl) ? (fromUrl as string) : (items[0]?.id ?? ''));
  const choose = (id: string) => {
    if (onChange) onChange(id);
    if (value === undefined) {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(param, id);
          return next;
        },
        { replace: true },
      );
    }
  };
  return [chosen, choose] as const;
}

export function Sections({ items, value, onChange, param = 'section', label = 'Sections', className }: SectionsProps) {
  const [chosen, choose] = useChosen(items, param, value, onChange);
  const body = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  useEffect(() => {
    // On a phone the menu is a row wider than the screen: keep the chosen
    // section's pill in it. Only the row scrolls, never the page.
    const row = menu.current;
    const pill = row?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!row || !pill || row.scrollWidth <= row.clientWidth) return;
    const left = pill.offsetLeft - row.offsetLeft;
    if (left < row.scrollLeft || left + pill.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = Math.max(0, left - 16);
    }
  }, [chosen]);

  useEffect(() => {
    // A section opened by the menu starts at its top — just under the fixed
    // header (`scroll-margin-top`) — unless the page is already above it.
    if (first.current) {
      first.current = false;
      return;
    }
    const element = body.current;
    const area = element?.closest('.simplebar-content-wrapper');
    if (!element || !area) return;
    const offset = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
    if (element.getBoundingClientRect().top < area.getBoundingClientRect().top + offset) {
      element.scrollIntoView({ block: 'start' });
    }
  }, [chosen]);

  const onInvalid = (event: FormEvent<HTMLDivElement>) => {
    const owner = (event.target as HTMLElement).closest<HTMLElement>('[data-section]');
    const id = owner?.dataset.section;
    if (id && id !== chosen) choose(id);
  };

  return (
    <div className={cn('md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:items-start md:gap-6', className)}>
      <nav
        aria-label={label}
        className={cn(
          'sticky top-(--page-sticky-top,0px) z-page-under bg-background',
          // A phone: a full-width row under the header. Wide: a column beside.
          'max-md:mx-[calc(var(--page-gutter,1rem)*-1)] max-md:mb-3 max-md:border-b max-md:border-line max-md:px-(--page-gutter,1rem) max-md:py-2',
          'md:py-1',
        )}
      >
        <ScrollArea viewportRef={menu}>
          <ul className="flex gap-1 md:flex-col">
            {items.map((item) => {
              const on = item.id === chosen;
              return (
                <li key={item.id} className="shrink-0">
                  <button
                    type="button"
                    aria-current={on ? 'true' : undefined}
                    onClick={() => choose(item.id)}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-control text-left text-sm transition-colors',
                      'max-md:rounded-pill max-md:border max-md:px-3 max-md:py-1.5 md:px-3 md:py-2',
                      on
                        ? 'bg-accent-subtle font-medium text-accent max-md:border-accent'
                        : 'text-muted hover:bg-surface-2 hover:text-body max-md:border-line',
                    )}
                  >
                    {item.icon ? <span className="mt-0.5 shrink-0 [&>svg]:size-4">{item.icon}</span> : null}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 whitespace-nowrap md:whitespace-normal">
                        {item.label}
                        {item.badge}
                      </span>
                      {item.hint ? (
                        <span className="mt-0.5 hidden text-2xs font-normal text-faint md:block">{item.hint}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </nav>
      <div ref={body} className="min-w-0 scroll-mt-(--page-sticky-top,0px)" onInvalidCapture={onInvalid}>
        {items.map((item) => (
          <section
            key={item.id}
            data-section={item.id}
            hidden={item.id !== chosen}
            aria-label={typeof item.label === 'string' ? item.label : undefined}
            className="space-y-4"
          >
            {item.content}
          </section>
        ))}
      </div>
    </div>
  );
}
